import { BackButton, SegmentedButton } from "components";
import { ErrorHelper } from "helpers";
import NameHelper from "helpers/NameHelper";
import { Application } from "models/Application";
import { UserAuthorities } from "models/auth/types";
import { ApplicationEventType } from "models/types";
import { useCurrentUser } from "providers/CurrentUserProvider";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import { ApplicationService } from "services/applicationService";
import { ConfirmResponsibleModal, ResponsibleUserButton } from "./components";
import ApplicationPageDetails from "./tabs/Details";
import ApplicationPageDocuments from "./tabs/Documents";
import ApplicationPageHistory from "./tabs/History";
import ApplicationPageNewRecord from "./tabs/NewRecord";
import {
	BackofficeApplicationPageParams,
	BackofficeApplicationPageTabs,
	TabContent
} from "./types";

const getSegmentsList = (
	tabs: Record<BackofficeApplicationPageTabs, TabContent>
): TabContent[] => {
	return Object.values(tabs);
};

export default function BackofficeApplicationPage(): JSX.Element {
	const navigate = useNavigate();
	const location = useLocation();
	const { applicationId, selectedTab } = useParams<
		keyof BackofficeApplicationPageParams
	>() as BackofficeApplicationPageParams;

	const setSelectedTab = useCallback(
		(newSelectedTab: BackofficeApplicationPageTabs) => {
			navigate(`/backoffice/solicitacao/${applicationId}/${newSelectedTab}`);
		},
		[navigate]
	);

	useEffect(() => {
		if (!Object.values(BackofficeApplicationPageTabs).includes(selectedTab)) {
			setSelectedTab(BackofficeApplicationPageTabs.DETAILS);
		}
	}, [selectedTab, setSelectedTab]);

	const user = useCurrentUser();
	const [isLoading, setIsLoading] = useState<boolean>(false);
	const [application, setApplication] = useState<Application>();
	const applicationService = useMemo(ApplicationService.getInstance, []);
	const [isResponsibleModalOpen, setIsResponsibleModalOpen] = useState(false);
	const [isLoadingSetResponsible, setIsLoadingSetResponsible] = useState(false);

	const loadApplicationDetails = useCallback(() => {
		setIsLoading(true);
		applicationService
			.getApplicationDetails(applicationId)
			.then((responseData) => {
				setApplication(responseData);
			})
			.catch((error) => ErrorHelper.showErrorMessage(error))
			.finally(() => setIsLoading(false));
	}, []);

	const isCurrentUserResponsible = useMemo(
		() => application?.isCurrentUserResponsible(user?.email),
		[application, user]
	);
	const isCurrentUserInResponsibleGroup = useMemo(
		() =>
			!!(
				user?.authorities &&
				application &&
				application.isCurrentUserInResponsibleGroup(user?.authorities)
			),
		[user?.authorities, application]
	);
	const userCanUpdateApplication = useMemo(
		() => user?.hasPermission(UserAuthorities.UPDATE),
		[user]
	);

	useEffect(() => {
		if (!isLoading) {
			loadApplicationDetails();
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [loadApplicationDetails]);

	const applicationTabs: Record<BackofficeApplicationPageTabs, TabContent> =
		useMemo(
			() => ({
				[BackofficeApplicationPageTabs.DETAILS]: {
					id: BackofficeApplicationPageTabs.DETAILS,
					onClick: () => setSelectedTab(BackofficeApplicationPageTabs.DETAILS),
					label: "Solicitação",
					component: ApplicationPageDetails
				},
				[BackofficeApplicationPageTabs.DOCUMENTS]: {
					id: BackofficeApplicationPageTabs.DOCUMENTS,
					onClick: () =>
						setSelectedTab(BackofficeApplicationPageTabs.DOCUMENTS),
					label: "Documentos",
					component: ApplicationPageDocuments
				},
				[BackofficeApplicationPageTabs.HISTORY]: {
					id: BackofficeApplicationPageTabs.HISTORY,
					onClick: () => setSelectedTab(BackofficeApplicationPageTabs.HISTORY),
					label: "Histórico",
					component: ApplicationPageHistory
				},
				[BackofficeApplicationPageTabs.NEW_RECORD]: {
					id: BackofficeApplicationPageTabs.NEW_RECORD,
					onClick: () =>
						setSelectedTab(BackofficeApplicationPageTabs.NEW_RECORD),
					label: "Novo registro",
					component: ApplicationPageNewRecord,
					disabled:
						!isCurrentUserResponsible ||
						application?.isInBOReadOnlyStatus() ||
						!userCanUpdateApplication,
					tooltip:
						!application?.hasUserResponsible() &&
						!application?.isInBOReadOnlyStatus() &&
						userCanUpdateApplication
							? "Você precisa tornar-se responsável para realizar um registro."
							: null
				}
			}),
			[application, isCurrentUserResponsible]
		);

	const currentTabContent: TabContent = useMemo(
		() => applicationTabs[selectedTab],
		[selectedTab]
	);
	const segments = useMemo(
		() => getSegmentsList(applicationTabs),
		[applicationTabs]
	);

	const reloadApplication = useCallback(() => {
		setApplication(undefined);
		loadApplicationDetails();
	}, [loadApplicationDetails, setApplication]);

	const onBecomeResponsibleUser = useCallback(() => {
		if (!application) return;
		setIsLoadingSetResponsible(true);
		const events = [
			{
				event: {
					type: ApplicationEventType.ASSUMIR_RESPONSABILIDADE
				}
			}
		];
		applicationService
			.registerEvents(application.id, events)
			.then(() => {
				toast.success("Agora você é responsável por esse processo");
				setIsResponsibleModalOpen(false);
				if (reloadApplication) reloadApplication();
			})
			.catch((error) => ErrorHelper.showErrorMessage(error))
			.finally(() => setIsLoadingSetResponsible(false));
	}, [application, applicationService, loadApplicationDetails]);

	return (
		<div className="flex flex-col w-full h-full">
			<div className="flex flex-col md:py-6 md:px-6 py-4 px-3 flex-1 md:max-h-[100%]">
				<div className="flex flex-row my-1">
					<SegmentedButton segments={segments} selected={selectedTab} />
				</div>
				<div className="flex flex-col md:flex-row justify-between mb-2 mt-8">
					<div className="flex flex-row items-center">
						<BackButton
							prevRoute={location.state?.prevRoute ?? "/backoffice"}
						/>
						<h1 className="ml-2 font-semibold md:text-xl flex items-center">
							{application
								? `${
										application.applicationProcessCode
								  } - ${application.applicationProprietorData?.proprietorName?.toUpperCase()}`
								: currentTabContent?.label}
						</h1>
					</div>
					{userCanUpdateApplication && (
						<ResponsibleUserButton
							hasResponsibleUser={application?.hasUserResponsible()}
							isCurrentUserResponsible={isCurrentUserResponsible}
							isApplicationInBOReadOnlyStatus={
								application?.isInBOReadOnlyStatus() ||
								!isCurrentUserInResponsibleGroup
							}
							responsibleUserName={NameHelper.abbreviateName(
								application?.currentResponsibleUserName ?? ""
							)}
							onClick={() => setIsResponsibleModalOpen(true)}
						/>
					)}
				</div>
				{currentTabContent && (
					<currentTabContent.component
						application={application}
						reloadApplication={reloadApplication}
						isBackoffice
					/>
				)}
				<ConfirmResponsibleModal
					isOpen={isResponsibleModalOpen}
					onClose={() => setIsResponsibleModalOpen(false)}
					onClickConfirm={onBecomeResponsibleUser}
					isLoadingConfirm={isLoadingSetResponsible}
				/>
			</div>
		</div>
	);
}
